<!--
  TagElement.vue

  Displays a single tag chip with optional parent hierarchy.

  Props:
    - value: Tag object (required) - Must have name, slug, and optionally color
    - showParent: Whether to display parent tag(s) (default: true)
    - readonly: Hide delete button when true (default: false)
    - fixed: Render as plain text (no links, no delete button) (default: false)
    - size: Chip size ('small', 'normal', 'medium', 'large')
    - draggable: Enable drag-drop functionality (default: false)

  Emits:
    - remove: Emitted when delete button is clicked with the tag
    - drag-start: Emitted when drag starts
    - drag-end: Emitted when drag ends
    - drop: Emitted when tag is dropped on this element
-->
<template>
  <div v-if="fixed" class="tags multiple m-0" :class="{ 'has-addons': hasAncestors }">
    <span
      v-for="ancestor in ancestors"
      :key="ancestor.id ?? undefined"
      class="tag parent"
      :class="tagClass"
      :style="styleFor(ancestor)"
    >
      {{ ancestor.name }}
    </span>
    <span class="tag" :class="tagClass" :style="styleFor(value)">
      {{ value.name }}
    </span>
  </div>

  <div
    v-else
    class="tags multiple m-0"
    :class="{
      'has-addons': hasAncestors || !readonly,
      'is-drag-over': isDragOver,
    }"
    :draggable="draggable"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <AugerLink
      v-for="ancestor in ancestors"
      :key="ancestor.id ?? undefined"
      class="tag parent"
      :class="tagClass"
      :style="styleFor(ancestor)"
      :target="tagTarget(ancestor)"
    >
      {{ ancestor.name }}
    </AugerLink>
    <AugerLink class="tag" :class="tagClass" :style="styleFor(value)" :target="tagTarget(value)">
      {{ value.name }}
    </AugerLink>
    <a
      v-if="!readonly"
      class="tag tag-delete"
      :class="tagClass"
      :style="deleteStyle"
      @click="handleDelete"
    ></a>
  </div>
</template>

<script setup lang="ts" generic="T extends TagValue">
import { computed, ref } from "vue"
import type { TagValueFieldsFragment } from "@auger/core/gql/types"
import AugerLink from "../link/AugerLink.vue"
import { tagTarget } from "../link/registry"

export interface TagValue {
  id?: number | null
  name: string
  slug?: string
  color?: string | null
  ancestors?: TagValue[] | null
}

/** What a dragged tag carries: the id and name handleDragStart writes. */
type DraggedTag = Pick<TagValueFieldsFragment, "id" | "name">

interface Props {
  value: T
  showParent?: boolean
  readonly?: boolean
  fixed?: boolean
  size?: string
  draggable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showParent: true,
  readonly: false,
  fixed: false,
  size: "normal",
  draggable: false,
})

const emit = defineEmits<{
  click: []
  remove: [tag: T]
  drop: [event: { draggedTag: DraggedTag; targetTag: T }]
}>()

const isDragOver = ref(false)

const ancestors = computed(() => {
  if (!props.showParent) return []
  return props.value?.ancestors ?? []
})

const hasAncestors = computed(() => ancestors.value.length > 0)

const deleteStyle = computed(() => {
  const darken = (x: number) => Math.max(0, Math.floor(x * 0.9))
  return styleForColor(adjustColor(props.value.color || "#bbbbbb", darken))
})

const tagClass = computed(() => (props.size !== "normal" ? `is-${props.size}` : ""))

function styleFor(tag: TagValue) {
  if (!tag.id) {
    return {
      backgroundColor: "#eee",
      color: "#666",
      fontStyle: "italic",
    }
  }
  return styleForColor(tag.color)
}

function handleDelete() {
  emit("remove", props.value)
}

function handleDragStart(e: DragEvent) {
  if (!props.draggable) return
  e.dataTransfer!.setData(
    "application/json",
    JSON.stringify({ id: props.value.id, name: props.value.name })
  )
  e.dataTransfer!.effectAllowed = "move"
}

function handleDragEnd() {
  isDragOver.value = false
}

function handleDragOver(e: DragEvent) {
  if (!props.draggable) return
  e.preventDefault()
  e.dataTransfer!.dropEffect = "move"
  isDragOver.value = true
}

function handleDragLeave() {
  isDragOver.value = false
}

function handleDrop(e: DragEvent) {
  if (!props.draggable) return
  e.preventDefault()
  isDragOver.value = false
  const data = e.dataTransfer!.getData("application/json")
  if (!data) return
  const draggedTag: DraggedTag = JSON.parse(data)
  // Don't allow dropping on self
  if (draggedTag.id === props.value.id) return
  emit("drop", { draggedTag, targetTag: props.value })
}

function styleForColor(color: string | null | undefined) {
  return {
    backgroundColor: color || "#bbb",
    color: getContrastColor(color || "#bbb"),
  }
}

function adjustColor(color: string, fn: (x: number) => number = (x) => x) {
  const rgb = parseColor(color).map(fn)
  return "#" + rgb.map((x) => x.toString(16).padStart(2, "0")).join("")
}

function parseColor(color: string) {
  let hex = color.replace("#", "") // If the color is in the format "#RRGGBB"
  hex =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex
  return [0, 2, 4].map((i) => parseInt(hex.substring(i, i + 2), 16))
}

function getContrastColor(hexColor: string) {
  const [r = 0, g = 0, b = 0] = parseColor(hexColor)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.8 ? "var(--auger-white)" : "var(--auger-text-inverted)"
}
</script>

<style>
/* Collapse parent tags when there are 3+ total tags */
.tags.multiple:has(.tag:nth-child(2)):not(:hover) .tag.parent {
  max-width: 0.5rem;
  overflow: hidden;
  color: transparent !important;
  transition:
    max-width 0.5s,
    color 0.3s,
    padding 0.8s;
  padding: 0;
}

/* Expand on hover */
.tags.multiple:hover .tag.parent {
  max-width: 12rem !important;
  transition:
    padding 0.8s,
    max-width 0.3s,
    color 0.5s;
}

.tag.parent + .tag {
  padding-left: 8px;
}

.tags.is-drag-over {
  outline: 2px dashed var(--auger-primary);
  outline-offset: 2px;
}

.tags[draggable="true"] {
  cursor: grab;
}
</style>
